<template>
	<div class="statuses-container search-container" v-show="showSearch">
		<div class="flex-statuses-floater flex-wrapper">
			<SearchOutlined class="search-icon" />
			<Input
				v-model:value="search"
				class="search-input"
				:bordered="false"
				:placeholder="$t('mapSearch.placeholder')"
				allowClear
				@keyup.enter="onEnter"
				@keydown="onKeyDown"
			/>
		</div>
		<ul v-show="foundMountpoints.length > 0" class="found-list" ref="foundList">
			<li v-for="mountpoint in foundMountpoints" :key="mountpoint.mountpoint">
				<router-link
					:data-index="mountpoint.index"
					:class="{ 'mountpoint-selected': mountpoint.selected }"
					:to="{ name: 'bs-on-map', params: { focusedMountpoint: mountpoint.mountpoint } }"
					@click="cleanSearch()"
				>
					{{ mountpoint.mountpoint }}
				</router-link>
			</li>
		</ul>
		<TypographyText v-if="showNoResults" type="secondary" class="no-results">
			{{ $t('mapSearch.noResults') }}
		</TypographyText>
	</div>
</template>

<script>
import { ref, computed, watch } from 'vue';

import { useRouter, useRoute } from 'vue-router';

import { useEventListener } from '@vueuse/core';

import { Input, TypographyText } from 'ant-design-vue';
import { SearchOutlined } from '@ant-design/icons-vue';

import { toCamelCase } from '@/api/ApiHelpers';
import Utils from '@/api/Utils';

import { useBaseStationsStore } from '@/stores/baseStations';
import { hasCoords } from '@/utils/baseStationsUtils';


const matchBaseStation = (baseStation, field, substr) => {
	const mountpoint = (baseStation[field] || '').toLowerCase();
	if (mountpoint === '') {
		return false;
	}
	return mountpoint.includes(substr);
};


const NO_SELECTION = -1;


export default {
	components: {
		Input, TypographyText, SearchOutlined,
	},
	setup() {
		const search = ref('');
		const selectedIndex = ref(NO_SELECTION);
		const trimmedSearch = computed(() => search.value.trim().toLowerCase());
		watch(trimmedSearch, () => {
			if (trimmedSearch.value === '') {
				selectedIndex.value = NO_SELECTION;
			}
		});
		watch(selectedIndex, value => {
			if (value === NO_SELECTION) {
				return;
			}
			const foundList = document.querySelector('.found-list');
			if (foundList) {
				const foundItem = foundList.children[value];
				if (foundItem) {
					foundItem.scrollIntoView({ block: 'nearest' });
				}
			}
		});
		const route = useRoute();
		const showSearch = computed(() => ['home-map', 'bs-on-map'].includes(route.name));
		const baseStationsStore = useBaseStationsStore();
		const foundList = ref();

		useEventListener(foundList, 'mousemove', event => {
			const index = Number.parseInt(event.target.dataset.index);
			if (!Number.isNaN(index)) {
				selectedIndex.value = index;
			}
		});
		const foundMountpoints = computed(() => {
			if (trimmedSearch.value === '') {
				return [];
			}
			let result = new Set();
			baseStationsStore.items.map(toCamelCase).filter(hasCoords).forEach(baseStation => {
				for (const field of ['mntpntInt', 'mntpntSrc']) {
					if (matchBaseStation(baseStation, field, trimmedSearch.value)) {
						result.add(baseStation[field]);
					}
				}
			});
			return Utils.sortArray([...result], Utils.multiSorter()).map((mountpoint, index) => ({
				index,
				mountpoint,
				selected: index === selectedIndex.value,
			}));
		});

		const showNoResults = computed(() => trimmedSearch.value.length > 0 && foundMountpoints.value.length === 0);

		const cleanSearch = () => {
			search.value = '';
		};

		const router = useRouter();
		const onEnter = () => {
			if (foundMountpoints.value.length === 0) {
				return;
			}
			const index = selectedIndex.value === NO_SELECTION ? 0 : selectedIndex.value;
			const focusedMountpoint = foundMountpoints.value[index].mountpoint;
			search.value = '';
			selectedIndex.value = NO_SELECTION;
			router.push({ name: 'bs-on-map', params: { focusedMountpoint } });
		}
		const onKeyDown = event => {
			if (event.key === 'ArrowUp') {
				event.preventDefault();
				if (selectedIndex.value > 0) {
					selectedIndex.value -= 1;
				}
			}
			if (event.key === 'ArrowDown') {
				event.preventDefault();
				if (selectedIndex.value < foundMountpoints.value.length - 1) {
					selectedIndex.value += 1;
				}
			}
		}
		return {
			showSearch,
			search,
			showNoResults,
			foundList,
			foundMountpoints,
			cleanSearch,
			onEnter,
			onKeyDown,
		}
	},
}
</script>
<style scoped>
@import '@/assets/floaters.css';

.search-container {
	padding: 0;
	overflow: hidden;
}

.search-icon {
	margin-inline: 4px;
}

.search-input {
	width: 240px;
}

.found-list {
	margin: 0;
	padding: 0 0 4px 0;
	list-style-type: none;
	overflow-y: auto;
	max-height: calc(100vh - 80px);
	scrollbar-width: thin;
}

.found-list::-webkit-scrollbar {
	width: 9px;
}

.found-list::-webkit-scrollbar-track {
	background: transparent;
}

.found-list::-webkit-scrollbar-thumb {
	background-color: rgba(155, 155, 155, 0.5);
	border-radius: 20px;
	border: transparent;
}

.found-list a {
	display: block;
	padding: 4px 8px 4px 12px;
}

.mountpoint-selected {
	background-color: var(--ant-primary-color, #1890ff);
	color: white;
}

.flex-statuses-floater {
	display: flex;
	align-items: center;
	padding: 4px 0px;
	padding-inline-start: 8px;
}

.no-results {
	display: block;
	padding-bottom: 8px;
	text-align: center;
}
</style>
